import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Sun, Moon, Monitor, Bell, Globe, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { usersApi } from '../../utils/api';
import { cn } from '../../lib/utils';

const GeneralSettings = () => {
  const { user, setUser } = useAuth();
  const { theme, setLightMode, setDarkMode, setSystemMode } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [language, setLanguage] = useState('en');
  const [selectedTheme, setSelectedTheme] = useState('system');

  useEffect(() => {
    if (user) {
      // Parse first name and last name from display_name or username
      const displayNameValue = user.display_name || user.username || '';
      const nameParts = displayNameValue.split(' ');
      
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
      setDisplayName(displayNameValue);
      setEmail(user.email || '');
      setEmailNotifications(user.email_notifications ?? true);
      setBrowserNotifications(user.browser_notifications ?? false);
      setLanguage(user.language || 'en');
    }
  }, [user]);

  useEffect(() => {
    if (theme === 'dark') {
      setSelectedTheme('dark');
    } else if (theme === 'light') {
      setSelectedTheme('light');
    } else {
      setSelectedTheme('system');
    }
  }, [theme]);

  const handleThemeChange = (newTheme) => {
    setSelectedTheme(newTheme);
    if (newTheme === 'light') {
      setLightMode();
    } else if (newTheme === 'dark') {
      setDarkMode();
    } else {
      setSystemMode();
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Combine first and last name for display_name
      const newDisplayName = `${firstName} ${lastName}`.trim() || firstName || lastName;

      // Update profile information
      await usersApi.update({
        display_name: newDisplayName,
      });

      // Update settings
      await usersApi.updateSettings({
        display_name: newDisplayName,
        email_notifications: emailNotifications,
        browser_notifications: browserNotifications,
        language: language,
      });

      // Update local user state
      setUser({
        ...user,
        display_name: newDisplayName,
        email_notifications: emailNotifications,
        browser_notifications: browserNotifications,
        language: language,
      });

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8 pb-8">
      <div>
        <h2 className="text-3xl font-bold text-text-dark dark:text-dark-text mb-2">General</h2>
        <p className="text-text-gray dark:text-dark-text-secondary">
          Manage your profile and preferences
        </p>
      </div>

      {/* User Profile Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Profile</h3>
        
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            <AvatarImage src={user?.avatar_url} alt={displayName || user?.username} />
            <AvatarFallback className="bg-primary/10 text-primary text-xl">
              {firstName?.[0] || user?.username?.[0] || 'U'}
            </AvatarFallback>
          </Avatar>
          <Button 
            variant="gradient" 
            size="sm"
          >
            <Upload className="h-4 w-4 mr-2" />
            Change Avatar
          </Button>
        </div>

        <div className="grid gap-4 max-w-2xl">
          {/* First Name and Last Name in a grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first-name">First Name</Label>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="last-name">Last Name</Label>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="display-name">Display Name</Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={email}
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-text-gray dark:text-dark-text-secondary">
              Contact support to change your email address
            </p>
          </div>
        </div>
      </div>

      <Separator />

      {/* Connected Accounts Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Connected Accounts</h3>
        
        <div className="max-w-2xl space-y-3">
          {/* Google Account - Always show */}
          <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg bg-white dark:bg-dark-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-text-dark dark:text-dark-text">Google</p>
                {user?.oauth_provider === 'google' ? (
                  <p className="text-sm text-text-gray dark:text-dark-text-secondary">{email}</p>
                ) : (
                  <p className="text-sm text-text-gray dark:text-dark-text-secondary">Not connected</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user?.oauth_provider === 'google' ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    Connected
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => toast.info('OAuth disconnect functionality coming soon')}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button 
                  variant="gradient"
                  size="sm"
                  onClick={() => window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/google`}
                >
                  Connect
                </Button>
              )}
            </div>
          </div>

          {/* GitHub Account - Always show */}
          <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-[#2e2e2d] rounded-lg bg-white dark:bg-dark-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path fill="currentColor" d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-text-dark dark:text-dark-text">GitHub</p>
                {user?.oauth_provider === 'github' ? (
                  <p className="text-sm text-text-gray dark:text-dark-text-secondary">{email}</p>
                ) : (
                  <p className="text-sm text-text-gray dark:text-dark-text-secondary">Not connected</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user?.oauth_provider === 'github' ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    Connected
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => toast.info('OAuth disconnect functionality coming soon')}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button 
                  variant="gradient"
                  size="sm"
                  onClick={() => window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/github`}
                >
                  Connect
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Notifications Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Notifications</h3>
        </div>

        <div className="space-y-4 max-w-md">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Receive email updates about your activity
              </p>
            </div>
            <Switch
              checked={emailNotifications}
              onCheckedChange={setEmailNotifications}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Browser Notifications</Label>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                Receive push notifications in your browser
              </p>
            </div>
            <Switch
              checked={browserNotifications}
              onCheckedChange={setBrowserNotifications}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Appearance Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Sun className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Appearance</h3>
        </div>

        <div className="space-y-2">
          <Label>Color Mode</Label>
          <div className="flex gap-3">
            <button
              onClick={() => handleThemeChange('light')}
              className={cn(
                "flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                selectedTheme === 'light'
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 dark:border-[#2e2e2d] hover:border-gray-300 dark:hover:border-gray-600"
              )}
            >
              <Sun className="h-5 w-5" />
              <span className="text-sm font-medium">Light</span>
            </button>

            <button
              onClick={() => handleThemeChange('dark')}
              className={cn(
                "flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                selectedTheme === 'dark'
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 dark:border-[#2e2e2d] hover:border-gray-300 dark:hover:border-gray-600"
              )}
            >
              <Moon className="h-5 w-5" />
              <span className="text-sm font-medium">Dark</span>
            </button>

            <button
              onClick={() => handleThemeChange('system')}
              className={cn(
                "flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                selectedTheme === 'system'
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 dark:border-[#2e2e2d] hover:border-gray-300 dark:hover:border-gray-600"
              )}
            >
              <Monitor className="h-5 w-5" />
              <span className="text-sm font-medium">System</span>
            </button>
          </div>
        </div>
      </div>

      <Separator />

      {/* Language Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">Language</h3>
        </div>

        <div className="max-w-md space-y-2">
          <Label htmlFor="language">Display Language</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger id="language">
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-4">
        <Button 
          variant="gradient"
          onClick={handleSave} 
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default GeneralSettings;
